import type { ReactNode } from 'react'

import { ProblemAlert } from '@/components/error-surface'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { ApiError } from '@/lib/problem-details'

/**
 * The shell every group command dialog is built in.
 *
 * It has two faces. Before the click it is a form with, where the action is dual-controlled, the
 * notice that says so. After a dual-controlled command it stops being a form and shows what
 * actually happened, because "sent for approval" is a different message from "done" and an
 * operator who sees a dialog vanish reads that as "done" whatever a toast says afterwards.
 *
 * A command that is not dual-controlled passes no `outcome` and simply closes on success, which
 * is the honest thing there: it did what it said.
 */
export interface GroupActionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: ReactNode
  /** Rendered above the form fields — the dual-control notice belongs here. */
  notice?: ReactNode
  children: ReactNode
  /**
   * Present once a dual-controlled command has answered. The form is replaced by it, so the
   * operator reads the result rather than the inputs that produced it.
   */
  outcome?: ReactNode
  confirmLabel: string
  tone?: 'default' | 'danger'
  isPending?: boolean
  /** True while the form is incomplete. */
  disabled?: boolean
  error?: ApiError | null
  onConfirm: () => void
}

export function GroupActionDialog({
  open,
  onOpenChange,
  title,
  description,
  notice,
  children,
  outcome,
  confirmLabel,
  tone = 'default',
  isPending = false,
  disabled = false,
  error = null,
  onConfirm,
}: GroupActionDialogProps) {
  const settled = outcome !== undefined && outcome !== null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {settled ? (
            outcome
          ) : (
            <>
              {notice}
              {children}
              {error ? <ProblemAlert error={error} /> : null}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          {settled ? (
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                Cancel
              </Button>
              <Button
                variant={tone === 'danger' ? 'danger' : 'primary'}
                onClick={onConfirm}
                disabled={isPending || disabled}
              >
                {isPending ? 'Working…' : confirmLabel}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
